import React, { useContext, useState, Fragment, useEffect } from "react";
import { Form as FinalForm, Field } from "react-final-form";
import { Container, Form, Button } from "semantic-ui-react";
import {
  combineValidators,
  composeValidators,
  isRequired,
  hasLengthLessThan,
  isNumeric,
  hasLengthGreaterThan,
  matchesPattern,
} from "revalidate";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { RootStoreContext } from "../../../app/stores/rootStore";
import { IClientForm } from "../../../app/models/client";
import TextInput from "../../../app/common/form/TextInput";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import SelectInput from "../../../app/common/form/SelectInput";
import { PlanTypeEnumOptions } from "../../../app/util/securityEnumsOptions";
import { Role } from "../../../app/util/role";

const validate = combineValidators({
  companyID: composeValidators(
    isRequired({ message: "El NIT es obligatorio" }),
    isNumeric({ message: "El NIT debe ser numerico" }),
    hasLengthGreaterThan(6)({
      message: "El NIT debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(11)({
      message: "El NIT debe tener máximo 10 caracteres",
    })
  )(),
  registrationName: composeValidators(
    isRequired({ message: "El Nombre o Razón Social es obligatorio" }),
    hasLengthGreaterThan(1)({
      message: "El Nombre o Razón Social debe tener al menos 2 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "El Nombre o Razón Social debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[A-zÀ-ÿ0-9 '.,;:&_@\-]*$/)({
      message:
        "Nombre no válido. Contiene caracteres especiales diferente a: ' . , ; : & _ @",
    })
  )(),
  planType: composeValidators(
    isRequired({ message: "El Plan es obligatorio" })
  )(),
});

interface IProps {
  id: string;
}

const EditClientForm: React.FC<IProps> = ({ id }) => {
  //variables para uso la informacion del userStore
  const rootStore = useContext(RootStoreContext);
  const {
    client,
    loadClient,
    updateClient,
    btEditClient,
  } = rootStore.clienStore;
  const { user } = rootStore.userStore;
  const { getSellersOption, sellersOption } = rootStore.sellerStore;

  useEffect(() => {
    if (user?.role === Role.Sudo) getSellersOption();
  }, [getSellersOption, user]);

  const [editClientForm] = useState<IClientForm>({
    sellerId: client.sellerId,
    companyID: client.companyID,
    registrationName: client.name,
    planType: client.planType,
    companyKey: client.companyKey,
  });

  const handleFinalFormSubmit = (values: any) => {
    if (
      values.sellerId !== editClientForm.sellerId ||
      values.companyID !== editClientForm.companyID ||
      values.registrationName !== editClientForm.registrationName ||
      values.planType !== editClientForm.planType
    ) {
      editClientForm.companyID = values.companyID.trim();
      editClientForm.registrationName = values.registrationName.trim();
      editClientForm.planType = values.planType;
      editClientForm.sellerId = values.sellerId;
      updateClient(editClientForm);
    } else toast.error("No ha hecho ningún cambio");
  };

  if (id !== "null" && !loadClient)
    return <LoadingComponent content="Cargando" />;

  return (
    <div className="tabContentStyle">
      <FinalForm
        validate={validate}
        initialValues={editClientForm}
        onSubmit={handleFinalFormSubmit}
        render={({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Container textAlign="justified">
              <label>Nombre Comercial</label>
              <Field
                name="registrationName"
                value={editClientForm.registrationName}
                component={TextInput}
              />
              <label>NIT del emisor</label>
              <Field
                name="companyID"
                value={editClientForm.companyID}
                component={TextInput}
              />
              <label>Plan</label>
              <Field
                name="planType"
                component={SelectInput}
                defaultValue={editClientForm.planType}
                placeholder="Seleccionar plan"
                options={PlanTypeEnumOptions}
              />
              {user?.role === Role.Sudo && (
                <Fragment>
                  <label>Vendedor</label>
                  <Field
                    name="sellerId"
                    component={SelectInput}
                    placeholder="Seleccionar vendedor"
                    defaultValue={editClientForm.sellerId}
                    options={sellersOption}
                  />
                </Fragment>
              )}
            </Container>
            <br />
            <Button
              loading={btEditClient}
              disabled={btEditClient}
              className="ui primary button"
              color="blue"
              type="submit"
              content="Guardar"
            />
          </Form>
        )}
      />
    </div>
  );
};

export default observer(EditClientForm);
